<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      :class="['modal', 'is-active', modalClass]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @keydown.esc="handleEsc"
    >
      <div class="modal-background" @click="handleBackgroundClick"></div>
      <div ref="modalRef" class="modal-card" :class="cardClass">
        <header v-if="$slots.header" class="modal-card-head">
          <slot name="header" />
          <button
            ref="closeButtonRef"
            class="delete"
            aria-label="close"
            @click="handleCancel"
          ></button>
        </header>
        <section v-if="$slots.default" class="modal-card-body">
          <slot />
        </section>
        <footer v-if="$slots.footer" class="modal-card-foot">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
  import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    closeOnEsc: { type: Boolean, default: true },
    closeOnBackgroundClick: { type: Boolean, default: true },
    manageFocus: { type: Boolean, default: true },
    modalClass: { type: String, default: "" },
    cardClass: { type: String, default: "" },
    titleId: { type: String, default: "" },
  });

  const emit = defineEmits(["update:modelValue", "close"]);

  const modalRef = ref(null);
  const closeButtonRef = ref(null);
  const previousActiveElement = ref(null);

  function handleCancel() {
    emit("update:modelValue", false);
    emit("close");
  }

  function handleEsc() {
    if (props.closeOnEsc) {
      handleCancel();
    }
  }

  function handleBackgroundClick() {
    if (props.closeOnBackgroundClick) {
      handleCancel();
    }
  }

  function handleKeydown(event) {
    if (!props.modelValue) return;

    if (event.key === "Tab") {
      const focusableElements = modalRef.value?.querySelectorAll(
        "button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), " +
          'textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  watch(
    () => props.modelValue,
    (isOpen) => {
      if (isOpen && props.manageFocus) {
        previousActiveElement.value = document.activeElement;
        nextTick(() => {
          closeButtonRef.value?.focus();
        });
      } else if (!isOpen && props.manageFocus) {
        nextTick(() => {
          if (
            previousActiveElement.value &&
            typeof previousActiveElement.value.focus === "function"
          ) {
            previousActiveElement.value.focus();
          }
        });
      }
    }
  );

  onMounted(() => {
    document.addEventListener("keydown", handleKeydown);
  });

  onUnmounted(() => {
    document.removeEventListener("keydown", handleKeydown);
  });
</script>
